import { Box, Divider, Typography, Card } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { ShieldCheck } from "lucide-react";

import { RoleConfiguration, Action, Breadcrumb } from "./components";
import useCreateRole from "./hooks/useCreateRole";
import { newRoleValidation } from "../../../../libs/form-validations";
import { Alert } from "../../../../shared";
import { useApiResponseParser, useAuth, useAuthorization } from "../../../../hooks";
import { PERMISSIONS } from "../../../../constants/permissions";

const INITIAL_VALUES = {
    Name: "",
    Description: "",
    Permissions: [],
};

export default function Create() {
    const { handleCreateRole, createRoleSvc } = useCreateRole();
    const { parseErrorResponseData } = useApiResponseParser();
    const { selectedOrganization } = useAuth();
    const { can } = useAuthorization();
    const canManage = can(PERMISSIONS.USERS_MANAGE);

    const methods = useForm({
        resolver: newRoleValidation,
        defaultValues: INITIAL_VALUES,
        mode: "onChange",
    });

    const onSubmit = methods.handleSubmit(async (data) => {
        if (!canManage) return;
        await handleCreateRole(data);
    });

    if (!canManage) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Only Admin/Owner can create roles." />
            </Box>
        );
    }

    if (!selectedOrganization?.Id) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Select an organization before creating a role." />
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider sx={{ mb: 2 }} />

            <Box ml={3} maxWidth={700}>
                <Box>
                    <Box display={"flex"} gap={1.5} alignItems={"center"} mb={1}>
                        <Card
                            variant="soft"
                            color="primary"
                            sx={{
                                p: 0.75,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "sm",
                            }}
                        >
                            <ShieldCheck size={22} style={{ display: "block" }} />
                        </Card>
                        <Typography level="title-lg">Define New Access Role</Typography>
                    </Box>
                    <Typography level="body-sm">
                        Create a specialized role for {selectedOrganization.Name}. Platform default roles
                        remain available separately and cannot be changed.
                    </Typography>
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Box>

            <Box component={"div"} ml={3} mt={3} width={700}>
                <FormProvider {...methods}>
                    <form onSubmit={onSubmit}>
                        <Box display={"flex"} gap={0} flexDirection={"column"}>
                            <RoleConfiguration />

                            <Box>
                                <Typography level="body-xs" sx={{ mt: 1, mb: 2, color: "text.secondary" }}>
                                    Review permissions carefully. Changes to roles affect all assigned users.
                                </Typography>
                                <Action api={createRoleSvc} />
                            </Box>

                            <Alert
                                successMessage={createRoleSvc.isSuccess && "Role provisioned successfully!"}
                                sx={{ mt: 1 }}
                                errorMessage={createRoleSvc.isError && parseErrorResponseData(createRoleSvc.error)}
                            />
                        </Box>
                    </form>
                </FormProvider>
            </Box>
        </Box>
    );
}
